import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { BarList, ColumnChart, Heatmap, LineChart, type ChartDatum } from "@/components/ui/chart";
import {
  DataTable, Figures, KpiRow, KpiTile, NONE, ReportSection, Segmented, ShareBar, tableExport, type DataColumn,
} from "@/features/report-kit";
import { useReport } from "@/lib/queries";
import {
  HANDOFF_MODE_LABELS, KIND_LABELS, binLabel, bucketLabel, bucketTitle, delta, fmtCompact, fmtCount, fmtHours, fmtPct,
  fmtSeconds, labelOf, type ReportFilters,
} from "@/lib/reports";
import type { LineReport, LineSeriesPoint, RepNumberRow, ReportLineRow, SkillRoutingRow } from "@/lib/types";

export interface TabProps {
  query: string;
  filters: ReportFilters;
  compareText: string;
  /** Narrow the report to something clicked in it. */
  onDrill: (patch: Partial<ReportFilters>) => void;
  onTruncated: (key: string, truncated: boolean) => void;
}

const LINE_COLUMNS: DataColumn<ReportLineRow>[] = [
  {
    key: "queue",
    header: "Line",
    value: (r) => r.description || r.queue,
    render: (r) => (
      <span className="flex flex-col leading-tight">
        <span className="inline-flex items-center gap-1.5 font-medium text-text">
          {r.description || r.queue}
          {r.deleted && (
            <Badge tone="outline" size="sm">
              Deleted
            </Badge>
          )}
        </span>
        <span className="text-2xs text-text-subtle">
          {r.campaign_name ?? (r.campaign_id ? `Deleted campaign · ${r.campaign_id.slice(0, 6)}` : "No campaign")}
          {r.purpose ? ` · ${r.purpose}` : ""}
        </span>
      </span>
    ),
  },
  { key: "offered", header: "Callers", align: "right", value: (r) => r.offered, render: (r) => fmtCount(r.offered) },
  { key: "answered", header: "Answered", align: "right", value: (r) => r.connected + r.dropped, render: (r) => fmtCount(r.connected + r.dropped) },
  { key: "abandoned", header: "Abandoned", align: "right", value: (r) => r.abandoned, render: (r) => fmtCount(r.abandoned) },
  { key: "timed_out", header: "Timed out", align: "right", value: (r) => r.timed_out, render: (r) => fmtCount(r.timed_out) },
  {
    key: "answer_rate",
    header: "Answer %",
    align: "right",
    value: (r) => r.answer_rate,
    render: (r) => (
      <>
        {fmtPct(r.answer_rate)}
        <ShareBar value={r.answer_rate} />
      </>
    ),
  },
  {
    key: "service_level",
    header: "Service level",
    align: "right",
    title: "Answered within the target, over answered + abandoned + timed out",
    value: (r) => r.service_level,
    render: (r) => fmtPct(r.service_level),
  },
  { key: "abandon_rate", header: "Abandon %", align: "right", value: (r) => r.abandon_rate, render: (r) => fmtPct(r.abandon_rate) },
  { key: "wait_avg", header: "Avg wait", align: "right", value: (r) => r.wait_avg, render: (r) => fmtSeconds(r.wait_avg) },
  { key: "wait_p90", header: "90% waited under", align: "right", value: (r) => r.wait_p90, render: (r) => fmtSeconds(r.wait_p90) },
  { key: "ring_avg", header: "Avg ring", align: "right", value: (r) => r.ring_avg, render: (r) => fmtSeconds(r.ring_avg) },
  { key: "ronas", header: "Rang out", align: "right", value: (r) => r.ronas, render: (r) => fmtCount(r.ronas) },
  { key: "talk_avg", header: "Avg talk", align: "right", value: (r) => r.talk_avg, render: (r) => fmtSeconds(r.talk_avg) },
  { key: "handoffs", header: "Handoffs in", align: "right", value: (r) => r.handoffs, render: (r) => fmtCount(r.handoffs) },
  { key: "relaxed", header: "Skill lifted", align: "right", title: "Callers the hard skill requirement was lifted for", value: (r) => r.relaxed, render: (r) => fmtCount(r.relaxed) },
];

function seriesColumns(granularity: string): DataColumn<LineSeriesPoint>[] {
  return [
    { key: "start", header: "Period", value: (p) => bucketTitle(p.start, granularity) },
    { key: "offered", header: "Callers", align: "right", value: (p) => p.offered },
    { key: "answered", header: "Answered", align: "right", value: (p) => p.answered },
    { key: "abandoned", header: "Abandoned", align: "right", value: (p) => p.abandoned },
    { key: "timed_out", header: "Timed out", align: "right", value: (p) => p.timed_out },
    { key: "answer_rate", header: "Answer %", align: "right", value: (p) => p.answer_rate, render: (p) => fmtPct(p.answer_rate) },
    { key: "service_level", header: "Service level", align: "right", value: (p) => p.service_level, render: (p) => fmtPct(p.service_level) },
    { key: "wait_avg", header: "Avg wait", align: "right", value: (p) => p.wait_avg, render: (p) => fmtSeconds(p.wait_avg) },
    { key: "talk_total", header: "Talk", align: "right", value: (p) => p.talk_total, render: (p) => fmtHours(p.talk_total) },
  ];
}

const SKILL_COLUMNS: DataColumn<SkillRoutingRow>[] = [
  { key: "slug", header: "Skill", value: (r) => r.name ?? r.slug },
  { key: "offered", header: "Callers", align: "right", value: (r) => r.offered },
  { key: "required", header: "Required", align: "right", value: (r) => r.required },
  { key: "preferred", header: "Preferred", align: "right", value: (r) => r.preferred },
  { key: "answer_rate", header: "Answer %", align: "right", value: (r) => r.answer_rate, render: (r) => fmtPct(r.answer_rate) },
  { key: "service_level", header: "Service level", align: "right", value: (r) => r.service_level, render: (r) => fmtPct(r.service_level) },
  { key: "wait_avg", header: "Avg wait", align: "right", value: (r) => r.wait_avg, render: (r) => fmtSeconds(r.wait_avg) },
  { key: "relaxed", header: "Waited until lifted", align: "right", value: (r) => r.relaxed },
  {
    key: "answered_by_holder",
    header: "Answered by a holder",
    align: "right",
    value: (r) => r.answered_by_holder,
    render: (r) => {
      const all = r.answered_by_holder + r.answered_by_other;
      return (
        <>
          {fmtCount(r.answered_by_holder)}
          <span className="ml-1 text-text-subtle">{all ? `(${fmtPct(r.answered_by_holder / all)})` : ""}</span>
        </>
      );
    },
  },
  {
    key: "answered_by_other",
    header: "By someone without it",
    align: "right",
    value: (r) => r.answered_by_other,
    render: (r) =>
      r.answered_by_other > 0 ? (
        <Badge tone="warning" size="sm">
          {fmtCount(r.answered_by_other)}
        </Badge>
      ) : (
        "0"
      ),
  },
];

const NUMBER_COLUMNS: DataColumn<RepNumberRow>[] = [
  { key: "rep", header: "Rep", value: (r) => r.name ?? r.rep },
  { key: "calls", header: "Callers", align: "right", value: (r) => r.calls },
  { key: "their_rep", header: "Got their rep", align: "right", value: (r) => r.their_rep },
  { key: "someone_else", header: "Got someone else", align: "right", value: (r) => r.someone_else },
  { key: "nobody", header: "Got nobody", align: "right", value: (r) => r.nobody },
  { key: "share", header: "Their rep %", align: "right", value: (r) => (r.calls ? r.their_rep / r.calls : null), render: (r) => fmtPct(r.calls ? r.their_rep / r.calls : null) },
];

type HeatMetric = "offered" | "abandon_rate" | "service_level" | "wait_avg";

export function LineReportView({ query, filters, compareText, onDrill, onTruncated }: TabProps) {
  const report = useReport<LineReport>("lines", query);
  const data = report.data;
  const loading = report.isPending;
  React.useEffect(() => onTruncated("lines", Boolean(data?.truncated)), [data?.truncated, onTruncated]);
  const [heat, setHeat] = React.useState<HeatMetric>("offered");
  const g = data?.window.granularity ?? "day";
  const t = data?.totals;
  const p = data?.previous ?? null;
  const series = data?.series ?? NONE;
  const points: ChartDatum[] = series.map((s) => ({
    key: s.start,
    label: bucketLabel(s.start, g),
    title: bucketTitle(s.start, g),
    values: {
      answered: s.answered,
      abandoned: s.abandoned,
      timed_out: s.timed_out,
      service_level: s.service_level,
      answer_rate: s.answer_rate,
    },
  }));
  const served = (x: typeof t) => (x ? x.connected + x.dropped : null);
  const handoffs = data?.handoffs;

  const columnsForSeries = React.useMemo(() => seriesColumns(g), [g]);
  const seriesExport = React.useMemo(() => tableExport(columnsForSeries, series), [columnsForSeries, series]);
  const linesExport = React.useMemo(() => tableExport(LINE_COLUMNS, data?.lines ?? []), [data?.lines]);
  const skillsExport = React.useMemo(() => tableExport(SKILL_COLUMNS, data?.skills ?? []), [data?.skills]);
  const numbersExport = React.useMemo(() => tableExport(NUMBER_COLUMNS, data?.rep_numbers.reps ?? []), [data?.rep_numbers.reps]);
  const waitRows = data?.waits ?? NONE;
  const waitsExport = React.useMemo(
    () =>
      tableExport(
        [
          { key: "bin", header: "Waited", value: (b: (typeof waitRows)[number]) => binLabel(b.lo, b.hi) },
          { key: "answered", header: "Answered", value: (b: (typeof waitRows)[number]) => b.answered },
          { key: "abandoned", header: "Abandoned", value: (b: (typeof waitRows)[number]) => b.abandoned },
          { key: "timed_out", header: "Timed out", value: (b: (typeof waitRows)[number]) => b.timed_out },
        ],
        waitRows,
      ),
    [waitRows],
  );
  const heatExport = React.useMemo(
    () =>
      tableExport(
        [
          { key: "weekday", header: "Weekday", value: (c: LineReport["heatmap"][number]) => c.weekday },
          { key: "hour", header: "Hour", value: (c: LineReport["heatmap"][number]) => c.hour },
          { key: "offered", header: "Callers", value: (c: LineReport["heatmap"][number]) => c.offered },
          { key: "answered", header: "Answered", value: (c: LineReport["heatmap"][number]) => c.answered },
          { key: "abandoned", header: "Abandoned", value: (c: LineReport["heatmap"][number]) => c.abandoned },
          { key: "service_level", header: "Service level", value: (c: LineReport["heatmap"][number]) => c.service_level },
          { key: "wait_avg", header: "Avg wait (s)", value: (c: LineReport["heatmap"][number]) => c.wait_avg },
        ],
        data?.heatmap ?? [],
      ),
    [data?.heatmap],
  );
  const handoffLines = handoffs?.by_line ?? NONE;
  const handoffExport = React.useMemo(
    () =>
      tableExport(
        [
          { key: "queue", header: "Closer line", value: (r: (typeof handoffLines)[number]) => r.queue },
          { key: "requested", header: "Asked for", value: (r: (typeof handoffLines)[number]) => r.requested },
          { key: "connected", header: "Connected", value: (r: (typeof handoffLines)[number]) => r.connected },
          { key: "cancelled", header: "Cancelled", value: (r: (typeof handoffLines)[number]) => r.cancelled },
          { key: "abandoned", header: "Caller hung up", value: (r: (typeof handoffLines)[number]) => r.abandoned },
          { key: "failed", header: "Failed", value: (r: (typeof handoffLines)[number]) => r.failed },
        ],
        handoffLines,
      ),
    [handoffLines],
  );

  const heatValue = (c: LineReport["heatmap"][number]): number | null =>
    heat === "offered" ? c.offered : heat === "wait_avg" ? c.wait_avg : c[heat];
  const heatFormat = heat === "offered" ? fmtCount : heat === "wait_avg" ? fmtSeconds : fmtPct;

  return (
    <div className="space-y-4">
      <KpiRow>
        <KpiTile
          label="Callers"
          loading={loading}
          value={fmtCompact(t?.offered)}
          hint={t ? `${fmtCount(served(t))} answered` : undefined}
          delta={delta(t?.offered, p?.offered, "count", "neutral")}
          deltaContext={compareText}
          spark={series.map((s) => s.offered)}
          info="Every stay in a line that began in the window. A caller handed from a setter to a closer is a stay in each line."
        />
        <KpiTile
          label="Answer rate"
          loading={loading}
          value={fmtPct(t?.answer_rate)}
          hint="of the callers the lines had to serve"
          delta={delta(t?.answer_rate, p?.answer_rate, "rate", "up")}
          deltaContext={compareText}
          spark={series.map((s) => s.answer_rate)}
          info="Answered (including a caller who hung up as the rep picked up) over callers the line had to serve: moved, removed and still-waiting callers are left out."
        />
        <KpiTile
          label="Service level"
          loading={loading}
          value={fmtPct(t?.service_level)}
          hint={data ? `answered within ${data.sla_seconds} s` : undefined}
          delta={delta(t?.service_level, p?.service_level, "rate", "up")}
          deltaContext={compareText}
          spark={series.map((s) => s.service_level)}
          info="Answered within the target, over answered + abandoned + timed out. A caller who hung up before the target counts against it."
        />
        <KpiTile
          label="Abandon rate"
          loading={loading}
          value={fmtPct(t?.abandon_rate)}
          hint={t ? `${fmtCount(t.abandoned)} hung up waiting` : undefined}
          delta={delta(t?.abandon_rate, p?.abandon_rate, "rate", "down")}
          deltaContext={compareText}
          spark={series.map((s) => s.abandon_rate)}
        />
        <KpiTile
          label="Average wait"
          loading={loading}
          value={fmtSeconds(t?.wait_avg)}
          hint={t?.wait_p90 != null ? `90% answered within ${fmtSeconds(t.wait_p90)}` : undefined}
          delta={delta(t?.wait_avg, p?.wait_avg, "seconds", "down")}
          deltaContext={compareText}
          spark={series.map((s) => s.wait_avg)}
          info="From joining the line to a rep, for the callers who were answered, on the ACD's own clock."
        />
        <KpiTile
          label="Average talk"
          loading={loading}
          value={fmtSeconds(t?.talk_avg)}
          hint={t ? `${fmtHours(t.talk_total)} on calls in all` : undefined}
          delta={delta(t?.talk_avg, p?.talk_avg, "seconds", "neutral")}
          deltaContext={compareText}
        />
        <KpiTile
          label="Rang out"
          loading={loading}
          value={fmtCount(t?.ronas)}
          hint="offers no rep picked up"
          delta={delta(t?.ronas, p?.ronas, "count", "down")}
          deltaContext={compareText}
        />
        <KpiTile
          label="Handoffs connected"
          loading={loading}
          value={fmtPct(handoffs?.connect_rate)}
          hint={handoffs ? `${fmtCount(handoffs.connected)} of ${fmtCount(handoffs.requested)} reached a closer` : undefined}
        />
      </KpiRow>

      <ReportSection
        id="callers-over-time"
        title="Callers over time"
        description="How many joined a line, and what happened to them."
        loading={loading}
        empty={!loading && (t?.offered ?? 0) === 0 ? "No callers joined a line in this window." : false}
        exportData={seriesExport}
        table={<DataTable rows={series} columns={columnsForSeries} rowKey={(r) => r.start} limit={60} />}
      >
        <div className="space-y-6">
          <ColumnChart
            data={points}
            series={[
              { key: "answered", label: "Answered", slot: 1 },
              { key: "abandoned", label: "Abandoned", slot: 2 },
              { key: "timed_out", label: "Timed out", slot: 3 },
            ]}
            format={fmtCount}
            ariaLabel="Callers per period, answered, abandoned and timed out"
          />
          <LineChart
            data={points}
            series={[
              { key: "service_level", label: "Service level", slot: 1 },
              { key: "answer_rate", label: "Answer rate", slot: 2 },
            ]}
            percent
            format={fmtPct}
            ariaLabel="Service level and answer rate per period"
          />
        </div>
      </ReportSection>

      <div className="grid gap-4 xl:grid-cols-2">
        <ReportSection
          id="when-callers-call"
          title="When callers call"
          description={`A week of hours on the report's clock (${filters.tz.replaceAll("_", " ")}).`}
          loading={loading}
          exportData={heatExport}
          actions={
            <Segmented
              label="Heatmap shows"
              value={heat}
              onChange={(v) => setHeat(v as HeatMetric)}
              options={[
                { id: "offered", label: "Callers" },
                { id: "abandon_rate", label: "Abandon %" },
                { id: "service_level", label: "Service level" },
                { id: "wait_avg", label: "Wait" },
              ]}
            />
          }
        >
          <Heatmap
            cells={(data?.heatmap ?? []).map((c) => ({
              weekday: c.weekday,
              hour: c.hour,
              value: heatValue(c),
              detail: `${fmtCount(c.offered)} callers · ${fmtPct(c.answer_rate)} answered · ${fmtSeconds(c.wait_avg)} avg wait`,
            }))}
            format={heatFormat}
            legend={heat === "offered" ? "More callers" : heat === "wait_avg" ? "Longer" : "Higher"}
            ariaLabel="Callers by weekday and hour"
          />
        </ReportSection>

        <ReportSection
          id="waits"
          title="How long callers waited"
          description="Answered or not. The tall bars on the right are the ones to fix."
          loading={loading}
          exportData={waitsExport}
          note={
            data
              ? `${fmtCount(data.abandons.abandoned)} abandoned: ${fmtCount(data.abandons.after_a_rep_rang)} after a rep's phone rang, ${fmtCount(data.abandons.before_any_rep_rang)} before any did. ${fmtCount(data.abandons.dropped_as_answered)} hung up as a rep answered.`
              : undefined
          }
        >
          <div className="space-y-4">
            <ColumnChart
              data={waitRows.map((b) => ({
                key: `${b.lo}`,
                label: binLabel(b.lo, b.hi),
                values: { answered: b.answered, abandoned: b.abandoned, timed_out: b.timed_out },
              }))}
              series={[
                { key: "answered", label: "Answered", slot: 1 },
                { key: "abandoned", label: "Abandoned", slot: 2 },
                { key: "timed_out", label: "Timed out", slot: 3 },
              ]}
              height={190}
              format={fmtCount}
              ariaLabel="Callers by how long they waited"
            />
            {data && (
              <Figures
                items={[
                  { label: "Abandoned after", value: fmtSeconds(data.abandons.wait_avg), hint: "on average" },
                  { label: "Half gave up within", value: fmtSeconds(data.abandons.wait_p50) },
                  {
                    label: "By call type",
                    value: Object.keys(data.abandons.by_kind).length
                      ? Object.entries(data.abandons.by_kind)
                          .map(([k, n]) => `${labelOf(KIND_LABELS, k)} ${n}`)
                          .join(" · ")
                      : "—",
                  },
                ]}
              />
            )}
          </div>
        </ReportSection>
      </div>

      <ReportSection
        id="lines"
        title="Lines"
        description="Every line with callers in the window. Click one to narrow the report to it."
        loading={loading}
        exportData={linesExport}
      >
        <DataTable
          rows={data?.lines ?? []}
          columns={LINE_COLUMNS}
          rowKey={(r) => r.queue}
          initialSort={{ key: "offered", dir: "desc" }}
          onRowClick={(r) => onDrill({ lines: [r.queue] })}
        />
      </ReportSection>

      <div className="grid gap-4 xl:grid-cols-2">
        <ReportSection
          id="skill-routing"
          title="Skill routing"
          description="Callers who needed a skill (Spanish, closer, …) and who answered them."
          loading={loading}
          exportData={skillsExport}
          empty={!loading && (data?.skills.length ?? 0) === 0 ? "No caller in this window needed a skill." : false}
          note="“A holder” is a rep who holds the skill today: skills keep no history, so a rep given one last week counts for last month's calls too."
          table={<DataTable rows={data?.skills ?? []} columns={SKILL_COLUMNS} rowKey={(r) => r.slug} />}
        >
          <BarList
            rows={(data?.skills ?? []).map((r) => ({
              key: r.slug,
              name: r.name ?? r.slug,
              label: r.name ?? r.slug,
              sublabel: `${fmtCount(r.offered)} callers · ${fmtPct(r.service_level)} in target · ${fmtCount(r.relaxed)} waited until lifted`,
              values: { holder: r.answered_by_holder, other: r.answered_by_other, lost: r.abandoned + r.timed_out },
            }))}
            series={[
              { key: "holder", label: "Answered by a holder", slot: 1 },
              { key: "other", label: "By someone without it", slot: 2 },
              { key: "lost", label: "Not answered", slot: 3 },
            ]}
            format={fmtCount}
            ariaLabel="Callers who needed each skill, by who answered"
          />
        </ReportSection>

        <ReportSection
          id="rep-numbers"
          title="Calls to a rep's own number"
          description="Callers who dialed a rep's number: did they reach that rep?"
          loading={loading}
          exportData={numbersExport}
          empty={!loading && (data?.rep_numbers.calls ?? 0) === 0 ? "Nobody called a rep's own number in this window." : false}
          table={<DataTable rows={data?.rep_numbers.reps ?? []} columns={NUMBER_COLUMNS} rowKey={(r) => r.rep} />}
        >
          <BarList
            rows={(data?.rep_numbers.reps ?? []).map((r) => ({
              key: r.rep,
              name: r.name ?? r.rep,
              label: r.name ?? r.rep,
              sublabel: r.rep.split("@")[0],
              values: { their_rep: r.their_rep, someone_else: r.someone_else, nobody: r.nobody },
            }))}
            series={[
              { key: "their_rep", label: "Got their rep", slot: 1 },
              { key: "someone_else", label: "Got someone else", slot: 2 },
              { key: "nobody", label: "Got nobody", slot: 3 },
            ]}
            format={fmtCount}
            ariaLabel="Callers to each rep's own number, by who took them"
          />
        </ReportSection>
      </div>

      <ReportSection
        id="handoffs"
        title="Handoffs to closers"
        description="Every time a setter sent a caller on, and how it ended."
        loading={loading}
        exportData={handoffExport}
        empty={!loading && (handoffs?.requested ?? 0) === 0 ? "No handoffs were asked for in this window." : false}
      >
        {handoffs && (
          <div className="space-y-5">
            <Figures
              items={[
                { label: "Asked for", value: fmtCount(handoffs.requested) },
                { label: "Connected", value: fmtPct(handoffs.connect_rate), hint: `${fmtCount(handoffs.connected)} reached a closer` },
                { label: "Wait for a closer", value: fmtSeconds(handoffs.wait_avg), hint: handoffs.wait_p90 != null ? `90% within ${fmtSeconds(handoffs.wait_p90)}` : undefined },
                {
                  label: "How they were sent",
                  value: Object.entries(handoffs.by_mode)
                    .map(([m, n]) => `${labelOf(HANDOFF_MODE_LABELS, m)} ${n}`)
                    .join(" · ") || "—",
                },
                { label: "To a named closer", value: fmtCount(handoffs.named_closer), hint: `${fmtCount(handoffs.any_closer)} to any closer` },
                { label: "Still going", value: fmtCount(handoffs.in_progress) },
              ]}
            />
            <BarList
              rows={handoffs.by_line.map((l) => ({
                key: l.queue,
                name: l.queue,
                label: l.queue,
                values: { connected: l.connected, cancelled: l.cancelled, abandoned: l.abandoned, failed: l.failed },
              }))}
              series={[
                { key: "connected", label: "Connected", slot: 1 },
                { key: "cancelled", label: "Cancelled", slot: 2 },
                { key: "abandoned", label: "Caller hung up", slot: 3 },
                { key: "failed", label: "Failed", slot: 4 },
              ]}
              format={fmtCount}
              ariaLabel="Handoffs by closer line and how they ended"
            />
          </div>
        )}
      </ReportSection>
    </div>
  );
}
